import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { filter, finalize, map, skip, take, switchMap, tap } from 'rxjs/operators';

import { ConfigurationService } from '@sondermind/configuration';
import { Logger } from '@sondermind/datadog-logging';
import { HttpService } from '@sondermind/http';
import { IAPIFetchResponse, IAPIListRequestOptions, IAPIListResponse } from '@sondermind/utilities/models-http';
import { ReferralState } from '@sondermind/utilities/models-referrals';

import { Appointment, IAppointmentApiResponse, ISchedulingSagaErrorApiResponse } from '../models/appointment.model';
import { isLivePendingPat } from '../models/appointment.predicates';

const SESSIONS_FILTER: Partial<IAPIListRequestOptions> = {
  filter: [
    { key: 'current_and_future_within_video', value: 'true' },
  ],
  page: 'disabled',
  view: 'with_provider_info',
  sort: [
    { key: 'start_time', asc: true }
  ]
};

@Injectable({
  providedIn: 'root'
})
export class AppointmentsHttpService {
  constructor(
    private http: HttpService,
    private configurationService: ConfigurationService,
    private logger: Logger,
  ) { }

  private store = new BehaviorSubject<Appointment[]>(null);
  private isLoadingSubject = new BehaviorSubject<boolean>(false);
  private inFlightRefreshes = 0;
  private allPATSessionsByProviderIdStore$ = new BehaviorSubject<Record<number, Appointment[]> | null>(null);

  /**
   * True while any `refresh()` request is outstanding. Lets a consumer hold off reading `store$` until
   * a refresh someone else started has landed, instead of rendering the pre-refresh value — the
   * dashboard depends on this to show a session booked from the direct-booking modal, which fires
   * its refresh before navigating.
   *
   * Refcounted, not a plain boolean: this service is `providedIn: 'root'` with ~13 `refresh()` call
   * sites (nav shell, video session, appointment app, care team, both dashboards). With a boolean, the
   * first of two overlapping refreshes to land would lower the flag while the second was still
   * outstanding, letting a gated consumer read a stale `store$` snapshot — the very race this flag
   * exists to close.
   */
  isLoading$ = this.isLoadingSubject.asObservable();

  lazyLoadedStore$ = this.store.pipe(
    switchMap((res) => {
      if (res !== null) {
        return of(res);
      }

      return this.refresh();
    })
  );

  store$ = this.store.asObservable().pipe(
    filter((res) => res !== null)
  );

  // nonPendingAppointments$ excludes events in created aasm state e.g. Pending PAT events
  nonPendingAppointments$ = this.lazyLoadedStore$.pipe(
    filter((res) => res !== null),
    map((res) => res.filter((appointment) => appointment.eventState !== ReferralState.created))
  );

  scheduledAppointments$ = this.lazyLoadedStore$.pipe(
    filter((res) => res !== null),
    map((res) => res.filter((appointment) => appointment.eventState === ReferralState.scheduled))
  );

  pendingPATAppointments$ = this.lazyLoadedStore$.pipe(
    filter((res) => res !== null),
    map((res) => res.filter((appointment) => isLivePendingPat(appointment)))
  );

  pendingPATSessionsByProviderId$: Observable<Record<number, Appointment>> = this.pendingPATAppointments$.pipe(
    map((appointments) => this.createAppointmentRecordByProviderId(appointments))
  );

  allPATSessionsByProviderId$: Observable<Record<number, Appointment[]>> = this.allPATSessionsByProviderIdStore$.pipe(
    switchMap((appointmentRecord) => (appointmentRecord
      ? of(appointmentRecord)
      : this.index(SESSIONS_FILTER).pipe(
        map((res) => {
          const record = this.createAppointmentsRecordByProviderId(res?.data ?? []);
          this.allPATSessionsByProviderIdStore$.next(record);
          return record;
        })
      ))
    )
  );

  allSchedulingErrors$: BehaviorSubject<number[] | null> = new BehaviorSubject<number[] | null>(null);

  refresh(): Observable<Appointment[]> {
    this.inFlightRefreshes += 1;
    if (this.inFlightRefreshes === 1) {
      this.isLoadingSubject.next(true);
    }
    // finalize, not the next handler: a consumer gated on isLoading$ would wait forever if a failed
    // request left the count raised. The error handler is required for the same reason — without it
    // an error here is an unhandled RxJS error rather than something the flag's owner accounts for.
    this.index(SESSIONS_FILTER).pipe(
      finalize(() => {
        this.inFlightRefreshes -= 1;
        if (this.inFlightRefreshes === 0) {
          this.isLoadingSubject.next(false);
        }
      })
    ).subscribe({
      next: (res) => {
        const data = res?.data ?? [];
        this.store.next(data);
        this.allPATSessionsByProviderIdStore$.next(this.createAppointmentsRecordByProviderId(data));
      },
      error: (err: unknown) => this.logger.error(
        'AppointmentsHttpService: failed to refresh appointments', { err }
      )
    });
    return this.store$.pipe(skip(1), take(1));
  }

  index(options: Partial<IAPIListRequestOptions> = {}): Observable<IAPIListResponse<Appointment>> {
    const url = `${this.configurationService.env.svcBase}/scheduling/calendar_events.json`;
    const params = this.http.generateListParams(options);
    return this.http
      .get<IAPIListResponse<IAppointmentApiResponse>>(url, params)
      .pipe(
        map((resp) => ({ ...resp, data: resp?.data?.map((res) => new Appointment(res)) ?? [] })),
        this.http.handleHttpError()
      );
  }

  fetchById(id: number): Observable<Appointment | null> {
    const url = `${this.configurationService.env.svcBase}/scheduling/calendar_events/${id}.json`;
    return this.http
      .get<IAPIFetchResponse<IAppointmentApiResponse>>(url)
      .pipe(
        map((res) => (res?.data ? new Appointment(res.data) : null)),
        this.http.handleHttpError()
      );
  }

  /**
   * Fetches the persona provider ids of providers with whom the client scheduled,
   * but an error occured during the scheduling process
   * @param ids Optionally takes in persona provider ids to check for errors with those specific providers
   * @returns a list of all persona provider ids (number)
   */
  fetchSchedulingErrors(ids?: number[]): Observable<number[]> {
    if (this.allSchedulingErrors$.value) {
      return this.allSchedulingErrors$.asObservable();
    }

    let url = `${this.configurationService.env.svcBase}/scheduling/saga_errors`;

    if (ids) {
      const urlparams = ids.map((id) => `persona_provider_ids[]=${id}`);
      url = url.concat(`?${urlparams.join('&')}`);
    }

    return this.http
      .get<IAPIFetchResponse<ISchedulingSagaErrorApiResponse>>(url)
      .pipe(
        tap((res) => this.allSchedulingErrors$.next(res?.data?.persona_provider_ids ?? [])),
        map((res) => res?.data?.persona_provider_ids ?? []),
        this.http.handleHttpErrorStrict()
      );
  }

  private createAppointmentRecordByProviderId(appointments: Appointment[]): Record<number, Appointment> {
    if (!appointments?.length) return {};

    return appointments.reduce((x, item) => {
      const obj = x;
      obj[item.personaProviderId] = item;
      return obj;
    }, {});
  }

  /** Returns an object with provider ids as keys and arrays of the provider's appointments as values */
  private createAppointmentsRecordByProviderId(appointments: Appointment[]): Record<number, Appointment[]> {
    if (!appointments?.length) return {};

    return appointments.reduce((x, item) => {
      const obj = x;

      if (obj[item.personaProviderId]) {
        obj[item.personaProviderId].push(item);
      } else {
        obj[item.personaProviderId] = [item];
      }

      return obj;
    }, {} as Record<number, Appointment[]>);
  }
}
